import {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  createRef,
} from 'react';
import { Space, Spin } from 'antd';

import isEmpty from 'lodash/isEmpty';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useBenguide from 'hooks/benguide';
import { changePlanOrder } from 'modules/benefitsWellness/util';
import MDVPlan from 'model/MDVPlan';
import MDVTypes from 'modules/benefitsWellness/enums/MDVTypes';
import iconEdit from 'images/icon-edit.svg';
import leftRoundIcon from 'images/icons/icon-left-round-arrow.svg';
import rightRoundIcon from 'images/icons/icon-right-round-arrow.svg';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import { BENEFIT_CATEGORY } from 'constants/commonConstants';
import { MDVCardTitles } from 'modules/benefitsWellness/components/MDVCardTitles/MDVCardTitles';
import MDVCardValues, {
  MDVCardValuesRef,
} from 'modules/benefitsWellness/components/MDVCardValues/MDVCardValues';
import MissingInfo from 'modules/benefitsWellness/components/MissingInfo/MissingInfo';
import { TableContext } from 'modules/benefitsWellness/components/TableContext/TableContextProvider';
import NoPlansText from 'modules/benefitsWellness/components/NoPlansText/NoPlansText';
import { listHRAPlans } from 'modules/taxAdvantaged/slices/hraSlice';

import { DV_PLAN_DOCUMENT_TYPE } from 'modules/resources/constants';
import PlanDocuments from 'modules/resources/components/PlanDocuments/PlanDocuments';
import ViewResourcesModal from 'components/ViewResourcesModal/ViewResourcesModal';
import { PLAN_RESOURCES } from 'constants/languageConstants';
import { getText } from 'utils/stringUtil';

import styles from './dentalCardWrapper.module.less';

type DentalCardWrapperProps = {
  onUpdatePlanList: Function;
  allPlans: MDVPlan[];
  reloadPlans: Function;
  showAlert: Function;
  isLoading: boolean;
};

const DentalCardWrapper: FC<DentalCardWrapperProps> = (
  props: DentalCardWrapperProps
) => {
  const { onUpdatePlanList, reloadPlans, allPlans, showAlert, isLoading } =
    props;
  const dispatch = useAppDispatch();
  const { reset: resetTableContext } = useContext(TableContext);
  const { dental, benefitWellness } = useAppSelector((state: any) => state);
  const language = useAppSelector((state) => state.benguide.data?.language);
  const { isTablet, isMobile } = useAppSelector((state) => state.layout);
  const { activePlanCompare, selectedPlanForCompare } = benefitWellness;
  const { inProgress, filteredPlanList } = dental;
  const benguide = useBenguide();
  const isEditMode = benguide.isEditMode;
  const plans = useMemo(() => {
    if (activePlanCompare) {
      resetTableContext();
      return filteredPlanList.filter((plan: MDVPlan) =>
        selectedPlanForCompare.includes(plan.id)
      ) as MDVPlan[];
    } else {
      resetTableContext();
      return filteredPlanList;
    }
    /*
    Adding resetTableContext as a dependency will cause a nasty infinite loop
    that is very hard to debug
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlanCompare, filteredPlanList, selectedPlanForCompare]);

  const [planViewCursor, setPlanViewCursor] = useState<number>(0);
  const [planDetailsHeight, setPlanDetailsHeight] = useState<number>(80);
  const [planWidths, setPlanWidths] = useState<number[]>([]);
  const valueRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardValuesRefs = useRef<React.RefObject<MDVCardValuesRef>[]>([]);

  useEffect(() => {
    cardValuesRefs.current = plans.map(() => createRef<MDVCardValuesRef>());
  }, [plans, plans.length]);

  useEffect(() => {
    setPlanViewCursor(0);
  }, [plans.length]);

  useEffect(() => {
    setPlanDetailsHeight(80);
    const widths = valueRefs.current.map((ref) => ref?.offsetWidth || 0);
    setPlanWidths(widths);
  }, [plans]);

  useEffect(() => {
    const { planYearId, employerId, benefitClasses } = benguide;
    dispatch(
      listHRAPlans(
        employerId,
        planYearId,
        benefitClasses,
        language === 'SPANISH'
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxPlanCards = useMemo(() => {
    if (isTablet && !isMobile) {
      return 2;
    }
    if (isMobile) {
      return plans.length;
    }
    return 4;
  }, [isTablet, isMobile, plans?.length]);

  if (inProgress) {
    return (
      <Space size="middle">
        <Spin size="large" />
      </Space>
    );
  }

  return (
    <div
      className={`${styles.dentalCardWrapper} ${
        !isEditMode ? styles.customMargin : ''
      }`}
    >
      {planViewCursor !== 0 && plans.length > 0 && (
        <img
          className={styles.leftRoundIcon}
          src={leftRoundIcon}
          alt="left"
          onClick={() => {
            setPlanViewCursor(planViewCursor - 1);
          }}
        />
      )}

      {plans.length > 0 ? (
        <MDVCardTitles
          planType={MDVTypes.DENTAL}
          onUpdatePlanList={onUpdatePlanList}
          addedPlanList={plans}
          allPlans={allPlans}
          reloadPlans={reloadPlans}
          allowHeaderTitle={true}
          showAlert={showAlert}
          isLoading={isLoading}
          detailsHeight={planDetailsHeight}
          planColumns={planWidths}
          cardValuesRefs={cardValuesRefs.current}
        />
      ) : (
        <NoPlansText text={'There are no plans that fit your criteria.'} />
      )}

      {plans
        .slice(planViewCursor, maxPlanCards + planViewCursor)
        .map((plan: any, index: number) => (
          <div ref={(el) => (valueRefs.current[index] = el)} key={index}>
            <MDVCardValues
              ref={cardValuesRefs.current[index]}
              key={index}
              dataIndex={index}
              plan={plan}
              planType={MDVTypes.DENTAL}
              planIds={plans.map((plan: any) => plan?.id)}
              attachedPlans={plans}
              moveLeft={() => {
                changePlanOrder(
                  index + planViewCursor,
                  index + planViewCursor - 1,
                  plans,
                  onUpdatePlanList
                );
              }}
              moveRight={() => {
                changePlanOrder(
                  index + planViewCursor,
                  index + planViewCursor + 1,
                  plans,
                  onUpdatePlanList
                );
              }}
              hideLeftMoveButton={
                index % maxPlanCards === 0 && planViewCursor === 0
              }
              hideRightMoveButton={index + planViewCursor === plans.length - 1}
              summarySection={
                <DentalSummarySection plan={plan} attachedPlans={plans} />
              }
              onHigherDetailsHeight={(number) => {
                setPlanDetailsHeight(number);
              }}
              detailsHeight={planDetailsHeight}
            />
          </div>
        ))}
      {maxPlanCards + planViewCursor < plans.length && (
        <img
          className={styles.rightRoundIcon}
          src={rightRoundIcon}
          alt="right"
          onClick={() => {
            setPlanViewCursor(planViewCursor + 1);
          }}
        />
      )}
    </div>
  );
};

type DentalSummarySectionProps = {
  plan: MDVPlan;
  attachedPlans: MDVPlan[];
};

const DentalSummarySection: FC<DentalSummarySectionProps> = ({
  plan,
}: DentalSummarySectionProps) => {
  const benguide = useBenguide();
  const isEditMode = benguide.isEditMode;
  const [isVisible, setIsVisible] = useState(false);
  const language = useAppSelector((state) => state.benguide.data?.language);

  if (
    isEditMode ||
    !isEmpty(plan.documents) ||
    !isEmpty(plan.additionalDocuments) ||
    !isEmpty(plan.additionalWeblinks)
  ) {
    return (
      <>
        <EditableHotspot
          alwaysVisible={false}
          editText="Edit"
          editIcon={<img src={iconEdit} alt="edit" />}
          customModal={(visible: boolean, onClose: Function) => (
            <PlanDocuments
              visible={visible}
              onClose={onClose}
              benefitKind={BENEFIT_CATEGORY.DENTAL.value}
              planDocumentTypes={DV_PLAN_DOCUMENT_TYPE}
              plan={plan}
            />
          )}
        >
          {!isEmpty(plan.documents) ||
          !isEmpty(plan.additionalDocuments) ||
          !isEmpty(plan.additionalWeblinks) ? (
            <div className={styles.planSummaryWrapper}>
              <div className={styles.viewSummary}>
                <span
                  className={styles.sbcLink}
                  onClick={() => setIsVisible(true)}
                >
                  {getText(PLAN_RESOURCES, language)}
                </span>
              </div>
            </div>
          ) : isEditMode ? (
            <div className={styles.missingInfo}>
              <MissingInfo missingInfoText="Missing Summary" />
            </div>
          ) : (
            <div className={styles.emptySummary}>-</div>
          )}
        </EditableHotspot>
        <ViewResourcesModal
          plan={plan}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          benefitKind={BENEFIT_CATEGORY.DENTAL.value}
        />
      </>
    );
  }
  return <></>;
};

export default DentalCardWrapper;
